import Logger from '../utilities/logger';
/** The name of the module */
export const name = 'Injector';
/** Available lifetime scopes for objects created  */
export var InjectorScope;
(function (InjectorScope) {
    InjectorScope[InjectorScope["Singleton"] = 0] = "Singleton";
    InjectorScope[InjectorScope["Factory"] = 1] = "Factory";
})(InjectorScope || (InjectorScope = {}));
/** Type guard for checking if a value is an InjectorScope type */
const isInjectorScope = (valueOrScope) => (typeof valueOrScope === 'string' || typeof valueOrScope === 'number') && valueOrScope in InjectorScope;
export const isInjectableKey = (key) => typeof key === 'string' && key.length > 0;
/** The maximum depth to traverse during a resolve() call */
const maxRecursionDepth = 5;
export default class Injector {
    /**
       * Registers a constructor as available for DI
       *
       * @param {String} key Key to be used for resolving and dependency loading
       * @param {Function} constructor Constructor to be instantiated when an object is requested
       * @param {Object} dependencies Dependencies required by the constructor being registered
       */
    static register({ key, constructor, dependencies = {}, 
    // If this is passed in when registering a module, it
    // overrides the requested scope when pulled later
    scope, }) {
        // Store the entry with the injector
        Injector.registered[key] = {
            module: constructor,
            dependencies,
            scope,
        };
        // Initialize the dependencies for the module
        Injector.constructors[key] = constructor;
        Injector.dependencies[key] = {};
        // Loop through all the supplied dependencies and add them to the dependencies object
        Object
            .entries(dependencies)
            .forEach(([dependencyName, dependency]) => {
            Injector.dependencies[key][dependencyName] = Injector.normalizeDependency(dependency);
        });
    }
    /**
       * Accepts either a proper dependency tuple [scope, options] or a scope and returns a proper dependency tuple
       *
       * @param {Array | String} val Dependency value to be normalized
       *
       * @returns {Array}
       */
    static normalizeDependency(val) {
        // If the value is an injector scope, then wrap it into to an array
        return isInjectorScope(val) ? [val] : val;
    }
    /**
       * Create a dependency tuple to pass into the injectable() function
       *
       * @param key Name of the module/constructor dependency
       * @param scope The scope/lifecycle of the dependency
       * @param params Parameters to pass into the constructor of the dependency
       */
    static createDependency(key, scope, params) {
        return {
            [key]: params ? [scope, params] : [scope],
        };
    }
    /**
       * Get dependency values for a provided module/constructor key
       *
       * @param key Name of the module/constructor dependency to get values for
       * @param dependencies The dependencies object to fetch data from
       */
    static getDependencyValues(key, dependencies) {
        if (key in dependencies) {
            return Object.entries(dependencies[key]).map(([dependencyName, dependencyProps]) => ({
                name: dependencyName,
                scope: dependencyProps[0],
                parameters: dependencyProps[1],
            }));
        }
        return [];
    }
    /**
       * Get the injector scope for a provided module/constructor key
       *
       * @param key Name of the module/constructor dependency to get the scope of
       * @param dependencies The dependencies object to fetch data from
       */
    static getDependencyScope(key, dependencies) {
        if (!dependencies[key] || !Array.isArray(dependencies[key])) {
            Logger.error(`Scope requested for invalid dependency: ${key}`);
        }
        return Injector.getDependencyScopeValue(dependencies[key]);
    }
    /**
       * Get the injector scope of a DependencyValue tuple
       *
       * @param value The DependencyValue tuple containing the scope to be fetched
       */
    static getDependencyScopeValue(value) {
        return value[0];
    }
    /**
       * Get dependency parameters for a provided module/constructor key
       *
       * @param key Name of the module/constructor to get the dependency parameters of
       * @param dependencies The dependencies object to fetch data from
       */
    static getDependencyParameters(key, dependencies) {
        return Array.isArray(dependencies[key]) ? Injector.getDependencyParameterValue(dependencies[key]) : {};
    }
    /**
       * Get the dependency parameters of a DependencyValue tuple
       *
       * @param value The DependencyValue tuple containing the dependency parameters to be fetched
       */
    static getDependencyParameterValue(value) {
        return value[1];
    }
    /**
       * Based on the injection scope provided returns either a static instance or a new instance for
       * the module/constructor key provided.
       *
       * @param key Name of the module/constructor to get
       * @param valueOrScope DependencyValue or InjectorScope to be used to determine injection scope
       */
    static get(key, valueOrScope) {
        const dependencyValue = Injector.normalizeDependency(valueOrScope);
        if (Injector.getDependencyScopeValue(dependencyValue) === InjectorScope.Singleton) {
            return Injector.getInstance(key, Injector.getDependencyParameterValue(dependencyValue));
        }
        return Injector.invoke(key, Injector.getDependencies(key), Injector.getDependencyParameterValue(dependencyValue));
    }
    /**
       * Get instances for all dependencies for a provided module/constructor
       *
       * @param key Name of the module/constructor to get dependencies for
       */
    static getDependencies(key) {
        const dependencies = [];
        // Loop through each dependency and store either a freshly-created instance or a singleton instance in the
        // array of dependencies sent to the requested constructor
        Injector.getDependencyValues(key, Injector.dependencies).forEach((dependency) => {
            if (dependency.scope === InjectorScope.Factory) {
                dependencies.push(Injector.resolve(dependency.name, dependency.parameters));
            }
            else {
                dependencies.push(Injector.getInstance(dependency.name, dependency.parameters));
            }
        });
        return dependencies;
    }
    /**
       * The way that all objects are created via DI in this module. The specified key will be used to fetch the proper
       * constructor, then all provided params will be passed into that constructor.
       *
       * @param {String} key Key specifying which object to create
       * @param {Object} params Params to be used for the object's instantiation
       *
       * @returns {Function}
       */
    static resolve(key, params = {}) {
        // Increment the current recursion depth
        Injector.depth += 1;
        // If we're getting too deep, then throw an error and return
        if (Injector.depth >= maxRecursionDepth) {
            Logger.error(`Maximum recursion level of ${maxRecursionDepth} reached`);
            Logger.trace();
            return;
        }
        // Make sure the constructor and dependency are properly configured or throw an error and return
        if (!Injector.dependencies[key] || !Injector.constructors[key]) {
            Logger.error(`Dependency not properly configured: ${key}`);
            return;
        }
        // Initialize array for storing the actual dependency instances
        const dependencies = [];
        // Loop through each dependency and store either a freshly-created instance or a singleton instance in the
        // array of dependencies sent to the requested constructor
        Injector.getDependencyValues(key, Injector.dependencies).forEach((dependency) => {
            const entry = Injector.registered[key];
            // If we've explicitly said we want this dep to be created via factory
            if (entry.scope === InjectorScope.Factory) {
                dependencies.push(Injector.resolve(dependency.name, dependency.parameters));
                return;
            }
            // If we've explicitly said we want this dep to be pulled via singleton
            if (entry.scope === InjectorScope.Singleton) {
                dependencies.push(Injector.getInstance(dependency.name, dependency.parameters));
                return;
            }
            // If the dependency said we want this dep to be created via factory
            if (dependency.scope === InjectorScope.Factory) {
                dependencies.push(Injector.resolve(dependency.name, dependency.parameters));
                return;
            }
            // If no other case met, treat as singleton.
            dependencies.push(Injector.getInstance(dependency.name, dependency.parameters));
        });
        // Decrement the current recursion depth
        Injector.depth -= 1;
        // Request the object to be created and return it
        // eslint-disable-next-line consistent-return
        return Injector.invoke(key, dependencies, params);
    }
    /**
       * Perform the actual instantiation to create an object via DI
       *
       * @param {String} key Key specifying which object to create
       * @param {Object[]} dependencies Instantiated objects required by the requested object
       * @param {Object} params Parameters to send to the requested object
       *
       * @returns {Object} Instantiated object
       */
    static invoke(key, dependencies, params) {
        // Get the constructor object and instantiate it
        if (Array.isArray(params)) {
            return new (Injector.getConstructor(key))(...dependencies, ...params);
        }
        return new (Injector.getConstructor(key))(...dependencies, params);
    }
    /**
       * Returns the specified constructors from the local constructors array
       *
       * @param {String} key Key specifying which constructor to fetch
       *
       * @returns {Function} Specified constructor
       */
    static getConstructor(key) {
        return Injector.constructors[key];
    }
    /**
       * Returns singleton instance for the requested object, and creates the instance if needed
       *
       * @param {String} key Key specifying which instance to fetch
       * @param {Object} params Parameters to be passed into object if instantiation is required
       *
       * @returns {Object} Singleton instance of object
       */
    static getInstance(key, params) {
        // If the instance hasn't been created yet, then create it
        if (!Injector.instances[key]) {
            Injector.instances[key] = Injector.resolve(key, params);
        }
        return Injector.instances[key];
    }
    static isDefinedAsSingleton(key) {
        return Injector.registered[key].scope === InjectorScope.Singleton;
    }
}
/** Registered objects with the injector */
Injector.registered = {};
/** The constructors used to create requested objects */
Injector.constructors = {};
/** The dependencies defined for each constructor */
Injector.dependencies = {};
/** Objects created as singleton instances */
Injector.instances = {};
/** Counter for depth count of the resolve() function */
Injector.depth = 0;
